import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AppSidebarContent } from "@/components/AppSidebarContent";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { EmailSetupDialog } from "./EmailSetupDialog";
import { EmailReAuthDialog } from "./EmailReAuthDialog";
import { EmailComposeDialog } from "./EmailComposeDialog";
import { EmailSettings } from "./EmailSettings";
import { SaveAttachmentToCustomerDialog } from "./SaveAttachmentToCustomerDialog";
import { CustomerQuickViewDialog } from "./CustomerQuickViewDialog";
import {
  Search,
  Edit,
  Archive,
  Trash,
  Star,
  ArrowLeft,
  Paperclip,
  MoreVertical,
  Reply,
  ReplyAll,
  Forward,
  RefreshCw,
  Menu,
  Inbox,
  Send,
  FileText,
  Folder,
  Mail,
  ChevronRight,
  Download,
  X,
  Settings,
  Eye,
  Loader2,
  FolderUp,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { format, isToday, isYesterday } from "date-fns";
import { de } from "date-fns/locale";

interface Email {
  id: string;
  subject: string;
  from_address: string;
  to_addresses: any;
  body_html?: string;
  body_text?: string;
  received_at: string;
  sent_at?: string;
  is_read: boolean;
  is_starred: boolean;
  attachments?: any;
  labels?: any;
  status?: string;
  message_id?: string;
  account_id?: string;
}

interface EmailAccount {
  id: string;
  email_address: string;
  display_name?: string;
  is_active: boolean;
}

interface ModernEmailClientProps {
  className?: string;
}

// Decode RFC 2047 encoded words (=?charset?encoding?text?=)
const decodeRFC2047 = (text: string): string => {
  if (!text) return '';
  
  // Join adjacent encoded words
  let processed = text.replace(/\?=\s+=\?/g, '?==?');
  
  return processed.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (match, charset, encoding, encoded) => {
    try {
      const charsetLower = charset.toLowerCase();
      if (encoding.toUpperCase() === 'B') {
        const cleaned = encoded.replace(/[\r\n\s]/g, '');
        const bytes = Uint8Array.from(atob(cleaned), c => c.charCodeAt(0));
        try {
          return new TextDecoder(charsetLower).decode(bytes);
        } catch {
          return new TextDecoder('utf-8').decode(bytes);
        }
      } else if (encoding.toUpperCase() === 'Q') {
        let decoded = encoded.replace(/_/g, ' ');
        decoded = decoded.replace(/=([0-9A-Fa-f]{2})/g, (_, hex: string) => 
          String.fromCharCode(parseInt(hex, 16))
        );
        try {
          const bytes = Uint8Array.from(decoded, (c: string) => c.charCodeAt(0));
          return new TextDecoder(charsetLower).decode(bytes);
        } catch {
          return decoded;
        }
      }
    } catch {}
    return match;
  });
};

// Decode email subject and sender name
const decodeEmailField = (field: string | null | undefined): string => {
  if (!field) return '';
  
  let decoded = field;
  
  // First decode RFC2047 encoded words
  decoded = decodeRFC2047(decoded);
  
  // Clean up any remaining encoding artifacts
  decoded = decoded.replace(/\s+/g, ' ').trim();
  
  return decoded;
};

// Get display name from email address
const extractDisplayName = (emailString: string): { name: string; email: string } => {
  if (!emailString) return { name: 'Unbekannt', email: '' };
  
  // Decode first
  const decoded = decodeEmailField(emailString);
  
  // Format: "Name <email@domain.com>" or "email@domain.com"
  const nameEmailMatch = decoded.match(/^"?([^"<]+)"?\s*<([^>]+)>$/);
  if (nameEmailMatch) {
    return { 
      name: nameEmailMatch[1].trim(), 
      email: nameEmailMatch[2].trim() 
    };
  }
  
  // Just email
  const emailMatch = decoded.match(/<([^>]+)>/);
  if (emailMatch) {
    const email = emailMatch[1];
    return { 
      name: email.split('@')[0], 
      email 
    };
  }
  
  // Plain email
  if (decoded.includes('@')) {
    return { 
      name: decoded.split('@')[0], 
      email: decoded 
    };
  }
  
  return { name: decoded, email: decoded };
};

// Fix UTF-8 encoding issues from incorrectly decoded emails
const fixEncoding = (text: string): string => {
  if (!text) return '';
  
  // Common UTF-8 mojibake fixes (Latin-1 interpreted as UTF-8)
  const fixes: [RegExp, string][] = [
    [/Ã¤/g, 'ä'], [/Ã¶/g, 'ö'], [/Ã¼/g, 'ü'],
    [/Ã„/g, 'Ä'], [/Ã–/g, 'Ö'], [/Ãœ/g, 'Ü'],
    [/ÃŸ/g, 'ß'], [/Ã©/g, 'é'], [/Ã¨/g, 'è'],
    [/Ã /g, 'à'], [/Ã¢/g, 'â'], [/Ã®/g, 'î'],
    [/Ã´/g, 'ô'], [/Ã»/g, 'û'], [/Ã§/g, 'ç'],
    [/Ã±/g, 'ñ'], [/Ã¡/g, 'á'], [/Ã­/g, 'í'],
    [/Ã³/g, 'ó'], [/Ãº/g, 'ú'], [/â‚¬/g, '€'],
    [/â€"/g, '–'], [/â€"/g, '—'], [/â€˜/g, "'"],
    [/â€™/g, "'"], [/â€œ/g, '"'], [/â€/g, '"'],
    [/â€¦/g, '…'], [/Â /g, ' '], [/Â©/g, '©'],
    [/Â®/g, '®'], [/Â°/g, '°'], [/Â·/g, '·'],
    // Phone number special chars
    [/âª/g, ''], [/â¬/g, ''], [/\u202A/g, ''], [/\u202C/g, ''],
    // Left-to-right and right-to-left marks
    [/\u200E/g, ''], [/\u200F/g, ''],
    [/\u2028/g, '\n'], [/\u2029/g, '\n'],
  ];
  
  let fixed = text;
  for (const [pattern, replacement] of fixes) {
    fixed = fixed.replace(pattern, replacement);
  }
  
  return fixed;
};

// Quoted-printable (RFC 2045) decoding for UTF-8 bodies
const decodeQuotedPrintableUtf8 = (content: string): string => {
  // Remove soft line breaks first
  const withoutSoftBreaks = content.replace(/=\r?\n/g, "");

  // Decode sequences like =C3=BC as UTF-8 bytes
  return withoutSoftBreaks.replace(/(=([0-9A-F]{2}))+/gi, (match) => {
    const hexMatches = match.match(/=([0-9A-F]{2})/gi);
    if (!hexMatches) return match;

    try {
      const bytes = hexMatches.map((hex) => parseInt(hex.substring(1), 16));
      return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
    } catch {
      return hexMatches
        .map((hex) => String.fromCharCode(parseInt(hex.substring(1), 16)))
        .join("");
    }
  });
};

// Decode Base64 content (often used in emails)
const decodeBase64Content = (content: string): string => {
  try {
    // Check if content looks like base64 (only base64 chars + padding)
    const cleanContent = content.replace(/\s/g, '');
    if (!/^[A-Za-z0-9+/]+=*$/.test(cleanContent)) return content;
    if (cleanContent.length < 20) return content;
    
    const decoded = atob(cleanContent);
    // Check if result is valid UTF-8 text
    const bytes = new Uint8Array(decoded.split('').map(c => c.charCodeAt(0)));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return content;
  }
};

const maybeDecodeQuotedPrintableUtf8 = (content: string): string => {
  if (!content) return "";
  if (!/=\r?\n|=[0-9A-F]{2}/i.test(content)) return content;
  return decodeQuotedPrintableUtf8(content);
};

// Attempt to decode base64 if the content looks encoded
const maybeDecodeBase64 = (content: string): string => {
  if (!content) return "";
  const trimmed = content.trim();
  
  // Remove any trailing non-base64 chars (like "A00" at the end which is sometimes added)
  // and clean whitespace
  const cleanedForCheck = trimmed.replace(/[\r\n\s]/g, '');
  
  // Check for base64 pattern - allow some trailing garbage
  // Base64 text often has line breaks every 76 chars
  const base64Match = cleanedForCheck.match(/^([A-Za-z0-9+/]+=*)/);
  if (base64Match && base64Match[1].length > 50) {
    // Try to decode the matched base64 portion
    const base64Part = base64Match[1];
    try {
      const decoded = atob(base64Part);
      // Convert to proper UTF-8
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      const utf8Decoded = new TextDecoder('utf-8').decode(bytes);
      
      // Accept decoded if it looks like readable text (not binary)
      if (utf8Decoded.length > 0) {
        const printableRatio = (utf8Decoded.match(/[\x20-\x7E\xA0-\xFF\n\r\t\u00A0-\u00FF\u0100-\u017F\u0400-\u04FF]/g) || []).length / utf8Decoded.length;
        if (printableRatio > 0.7) {
          return utf8Decoded;
        }
      }
    } catch (e) {
      // Decoding failed, return original
    }
  }
  
  // Also check if content has multiple lines that each look like base64
  const lines = trimmed.split(/[\r\n]+/).filter(l => l.trim());
  if (lines.length > 1) {
    const allLinesBase64 = lines.every(line => {
      const cleanLine = line.trim();
      return cleanLine.length > 0 && /^[A-Za-z0-9+/]+=*$/.test(cleanLine);
    });
    
    if (allLinesBase64) {
      const combinedBase64 = lines.map(l => l.trim()).join('');
      try {
        const decoded = atob(combinedBase64);
        const bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i++) {
          bytes[i] = decoded.charCodeAt(i);
        }
        const utf8Decoded = new TextDecoder('utf-8').decode(bytes);
        
        if (utf8Decoded.length > 0) {
          const printableRatio = (utf8Decoded.match(/[\x20-\x7E\xA0-\xFF\n\r\t\u00A0-\u00FF\u0100-\u017F\u0400-\u04FF]/g) || []).length / utf8Decoded.length;
          if (printableRatio > 0.7) {
            return utf8Decoded;
          }
        }
      } catch (e) {
        // Decoding failed
      }
    }
  }
  
  return content;
};

const looksLikeHtmlEmail = (content: string): boolean => {
  if (!content) return false;
  const c = content.toLowerCase();
  if (c.includes("<!doctype") || c.includes("<html") || c.includes("<body")) return true;
  const tagHits = (c.match(/<\/?(p|br|div|span|table|tr|td|th|thead|tbody|img|a|head|meta|style)\b/g) || []).length;
  return tagHits >= 3;
};

const htmlToText = (html: string): string => {
  const normalized = html
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/<\/?p\b[^>]*>/gi, "\n")
    .replace(/<\/?div\b[^>]*>/gi, "\n")
    .replace(/<\/?li\b[^>]*>/gi, "\n")
    .replace(/<\/?h[1-6]\b[^>]*>/gi, "\n\n");

  try {
    if (typeof DOMParser === "undefined") {
      return normalized.replace(/<[^>]*>/g, " ").replace(/\s{2,}/g, " ").trim();
    }
    const doc = new DOMParser().parseFromString(normalized, "text/html");
    const text = doc.body?.textContent ?? "";
    return text
      .replace(/\u00A0/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  } catch {
    return normalized.replace(/<[^>]*>/g, " ").replace(/\s{2,}/g, " ").trim();
  }
};

const extractHtmlBody = (html: string): string => {
  try {
    if (typeof DOMParser === "undefined") return html;
    if (!/<!doctype|<html|<body/i.test(html)) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const body = doc.body?.innerHTML?.trim();
    return body && body.length > 0 ? body : html;
  } catch {
    return html;
  }
};

const extractMimeSection = (
  raw: string,
  kind: "text/plain" | "text/html"
): string | null => {
  if (!raw) return null;

  const normalized = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const kindRe = kind === "text/plain" ? "text\\/plain" : "text\\/html";

  // Try to find the content section with optional base64/quoted-printable encoding header
  const reWithEncoding = new RegExp(
    `Content-Type:\\s*${kindRe}[^\\n]*\\n(?:Content-Transfer-Encoding:\\s*(base64|quoted-printable)[^\\n]*\\n)?(?:[^\\n]*\\n)*?\\n([\\s\\S]*?)(?=\\n-{2,}[_A-Za-z0-9=.-]+(?:--)?\\s*(?:\\n|$)|\\nContent-Type:|$)`,
    "i"
  );

  const matchWithEnc = normalized.match(reWithEncoding);
  if (matchWithEnc) {
    const encoding = matchWithEnc[1]?.toLowerCase();
    let content = matchWithEnc[2]?.trim();
    
    if (content && encoding === 'base64') {
      content = decodeBase64Content(content);
    } else if (content && encoding === 'quoted-printable') {
      content = decodeQuotedPrintableUtf8(content);
    }
    
    if (content && content.length > 0) return content;
  }

  // Fallback: simpler pattern
  const re = new RegExp(
    `Content-Type:\\s*${kindRe}[^\\n]*\\n(?:[^\\n]*\\n)*?\\n([\\s\\S]*?)(?=\\n-{2,}[_A-Za-z0-9=.-]+(?:--)?\\s*(?:\\n|$)|\\nContent-Type:|$)`,
    "i"
  );

  const match = normalized.match(re);
  const part = match?.[1]?.trim();
  return part && part.length > 0 ? part : null;
};

const extractHtmlSegmentFromRaw = (raw: string): string | null => {
  if (!raw) return null;
  
  // First try to decode if it looks like base64
  let normalized = maybeDecodeBase64(raw);
  normalized = normalized.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  
  const lower = normalized.toLowerCase();
  const doctypeIdx = lower.indexOf("<!doctype");
  const htmlIdx = lower.indexOf("<html");
  const bodyIdx = lower.indexOf("<body");
  
  // Find the earliest valid starting point
  const candidates = [doctypeIdx, htmlIdx, bodyIdx].filter(i => i !== -1);
  if (candidates.length === 0) return null;
  
  const start = Math.min(...candidates);
  let extracted = normalized.slice(start).trim();
  
  // If we started with <body>, wrap it minimally
  if (start === bodyIdx && doctypeIdx === -1 && htmlIdx === -1) {
    // Extract body content if we can
    const bodyMatch = extracted.match(/<body[^>]*>([\s\S]*?)(?:<\/body>|$)/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
  }
  
  return extracted;
};

const unescapeCommonBodyTags = (html: string): string => {
  if (!html) return "";
  let out = html;

  // Only unescape a small safe subset of tags that sometimes appear as &lt;p&gt;...&lt;/p&gt;
  out = out.replace(
    /&lt;(\/?)(p|br|div|span|strong|b|em|i|u|ul|ol|li|blockquote|hr)\b/gi,
    "<$1$2"
  );
  out = out.replace(/&gt;/g, ">" );

  return out;
};

// Clean and format HTML email content - preserve original styling
const formatEmailHtml = (html: string | null | undefined): string => {
  if (!html) return "";

  // Try base64 decoding first if content looks encoded
  let rawContent = maybeDecodeBase64(html);
  
  // If we got a raw MIME/multipart body, extract the HTML part first
  const htmlPart =
    extractMimeSection(rawContent, "text/html") ??
    extractHtmlSegmentFromRaw(rawContent) ??
    rawContent;

  const isFullDocument = /<!doctype|<html\b|<head\b/i.test(htmlPart);

  // Decode quoted-printable & fix encoding issues
  let cleaned = fixEncoding(maybeDecodeQuotedPrintableUtf8(htmlPart));
  
  // Try base64 decode again on the extracted part
  cleaned = maybeDecodeBase64(cleaned);

  // Some senders embed the original text as escaped HTML (e.g. &lt;p&gt;...)
  cleaned = unescapeCommonBodyTags(cleaned);

  // IMPORTANT: Only extract <body> when we don't have a full HTML document.
  // Otherwise we would drop <head><style>…</style></head> and many emails render blank/unformatted.
  if (!isFullDocument) {
    cleaned = extractHtmlBody(cleaned);
  }

  // Remove dangerous elements but keep everything else
  cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  cleaned = cleaned.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, "");

  // Neutralize javascript: URLs
  cleaned = cleaned.replace(
    /\s(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi,
    ' $1="#"'
  );

  // Handle CID references - show a placeholder for embedded images
  cleaned = cleaned.replace(
    /<img([^>]*?)src=["']cid:([^"']+)["']([^>]*)>/gi,
    '<span style="display:inline-block;padding:8px 12px;background:#f0f0f0;border:1px dashed #ccc;border-radius:4px;color:#666;font-size:12px;">📷 Eingebettetes Bild: $2</span>'
  );

  // Fix empty src attributes
  cleaned = cleaned.replace(
    /src=["']["']/g,
    'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"'
  );

  return cleaned;
};

// Convert URLs in text to clickable links
const linkifyText = (text: string): React.ReactNode[] => {
  if (!text) return [];
  
  // Clean up URL-encoded characters that might be broken
  let cleanedText = text
    .replace(/=\r?\n/g, '') // Remove soft line breaks from quoted-printable
    .replace(/=3D/gi, '=')  // Fix encoded equals
    .replace(/=26/gi, '&')  // Fix encoded ampersand
    .replace(/=3F/gi, '?')  // Fix encoded question mark
    .replace(/=2F/gi, '/')  // Fix encoded slash
    .replace(/=3A/gi, ':'); // Fix encoded colon
  
  // URL regex - matches http, https, and www URLs
  const urlRegex = /(https?:\/\/[^\s<>"\)]+|www\.[^\s<>"\)]+)/gi;
  
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = urlRegex.exec(cleanedText)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push(cleanedText.substring(lastIndex, match.index));
    }
    
    // Clean up the URL (remove trailing punctuation that's not part of URL)
    let url = match[0].replace(/[.,;:!?\)]+$/, '');
    
    // Ensure URL has protocol
    const href = url.startsWith('http') ? url : `https://${url}`;
    
    // Add the clickable link
    parts.push(
      <a 
        key={`link-${keyIndex++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {url.length > 60 ? url.substring(0, 60) + '...' : url}
      </a>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < cleanedText.length) {
    parts.push(cleanedText.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : [text];
};

// Create readable text from email body
const formatEmailText = (text: string | null | undefined): string => {
  if (!text) return "";

  // First try to decode base64 if the entire content looks encoded
  let processedText = maybeDecodeBase64(text);

  const plainPart = extractMimeSection(processedText, "text/plain");
  const htmlPart = extractMimeSection(processedText, "text/html") ?? extractHtmlSegmentFromRaw(processedText);

  // Prefer the plain part if available, otherwise fall back to the full content
  let cleaned = fixEncoding(maybeDecodeQuotedPrintableUtf8(plainPart ?? processedText));
  
  // Also try base64 decode on the cleaned content if it still looks encoded
  cleaned = maybeDecodeBase64(cleaned);

  // If we don't have a plain part but do have HTML, convert HTML to readable text
  if (!plainPart && htmlPart) {
    const decodedHtml = fixEncoding(maybeDecodeQuotedPrintableUtf8(htmlPart));
    cleaned = htmlToText(decodedHtml);
  } else if (looksLikeHtmlEmail(cleaned)) {
    cleaned = htmlToText(cleaned);
  }

  // Decode common HTML entities (for text view only)
  cleaned = cleaned
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"');

  // Remove MIME boundaries and header lines
  cleaned = cleaned.replace(/^\s*-{2,}[_A-Za-z0-9=.-]+(?:--)?\s*$/gm, "");
  cleaned = cleaned.replace(/Content-Type:[^\n]+\n?/gi, "");
  cleaned = cleaned.replace(/Content-Transfer-Encoding:[^\n]+\n?/gi, "");
  cleaned = cleaned.replace(/Content-Disposition:[^\n]+\n?/gi, "");
  cleaned = cleaned.replace(/Content-ID:[^\n]+\n?/gi, "");
  cleaned = cleaned.replace(/MIME-Version:[^\n]+\n?/gi, "");

  // Remove IMAP debug artifacts that sometimes leak into stored bodies
  cleaned = cleaned.replace(/^\s*BODY\[[^\]]+\]\s*\{\d+\}\s*$/gmi, "");
  cleaned = cleaned.replace(/^\s*\)\s*$/gm, "");

  // Clean up whitespace
  cleaned = cleaned.replace(/\r/g, "");
  cleaned = cleaned.replace(/^[ \t]+$/gm, "");
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n").trim();

  return cleaned;
};

const buildEmailIframeContent = (html: string, baseCss: string): string => {
  const normalized = (html ?? "").trim();
  if (!normalized) return "";

  const isFullDocument = /<!doctype|<html\b/i.test(normalized);

  // If it's not a full document, wrap it.
  if (!isFullDocument) {
    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    ${baseCss}
    <style>
      body {
        margin: 0;
        padding: 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        background: #ffffff;
        color: #111111;
      }
    </style>
  </head>
  <body>${normalized}</body>
</html>`;
  }

  // Full document: preserve <head> styles, but rebuild a safe wrapper to avoid lost styles / CSP meta issues.
  try {
    if (typeof DOMParser === "undefined") {
      // Best-effort: inject responsive css into head.
      return normalized.replace(/<\/head>/i, `${baseCss}</head>`);
    }

    const doc = new DOMParser().parseFromString(normalized, "text/html");

    let headInner = doc.head?.innerHTML ?? "";
    const bodyInner = doc.body?.innerHTML?.trim() || "";

    // CSP meta tags can block external images/fonts inside srcDoc -> remove them
    headInner = headInner.replace(
      /<meta[^>]+http-equiv=["']?Content-Security-Policy["']?[^>]*>/gi,
      ""
    );

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    ${baseCss}
    <style>
      body {
        margin: 0;
        padding: 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        background: #ffffff;
        color: #111111;
      }
    </style>
    ${headInner}
  </head>
  <body>${bodyInner || normalized}</body>
</html>`;
  } catch {
    // Fallback: render as-is with responsive injection
    return normalized.replace(/<\/head>/i, `${baseCss}</head>`);
  }
};

// Swipeable Email Item Component
interface SwipeableEmailItemProps {
  email: Email;
  isSelected: boolean;
  onSelect: (email: Email) => void;
  onToggleStar: (email: Email, e?: React.MouseEvent) => void;
  onSwipeDelete: (email: Email) => void;
  onMarkAsUnread: (email: Email) => void;
  getInitials: (emailAddress: string) => string;
  getSenderName: (emailAddress: string) => string;
  formatDate: (dateString: string | null | undefined) => string;
  formatEmailText: (text: string) => string;
}

const SwipeableEmailItem = ({
  email,
  isSelected,
  onSelect,
  onToggleStar,
  onSwipeDelete,
  onMarkAsUnread,
  getInitials,
  getSenderName,
  formatDate,
  formatEmailText
}: SwipeableEmailItemProps) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const isMouseDown = useRef(false);

  const SWIPE_THRESHOLD_DELETE = -80;
  const SWIPE_THRESHOLD_UNREAD = 80;

  // Touch Events
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping) return;
    
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    // Allow swiping both directions
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, -120));
    } else if (diff > 0) {
      setSwipeOffset(Math.min(diff, 120));
    }
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
    
    if (swipeOffset < SWIPE_THRESHOLD_DELETE) {
      setSwipeOffset(-300);
      setTimeout(() => {
        onSwipeDelete(email);
      }, 200);
    } else if (swipeOffset > SWIPE_THRESHOLD_UNREAD) {
      setSwipeOffset(0);
      onMarkAsUnread(email);
    } else {
      setSwipeOffset(0);
    }
  };

  // Mouse Events for Desktop
  const handleMouseDown = (e: React.MouseEvent) => {
    touchStartX.current = e.clientX;
    touchCurrentX.current = e.clientX;
    isMouseDown.current = true;
    setIsSwiping(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown.current) return;
    
    touchCurrentX.current = e.clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    
    // Allow swiping both directions
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, -120));
    } else if (diff > 0) {
      setSwipeOffset(Math.min(diff, 120));
    }
  };

  const handleMouseUp = () => {
    if (!isMouseDown.current) return;
    isMouseDown.current = false;
    setIsSwiping(false);
    
    if (swipeOffset < SWIPE_THRESHOLD_DELETE) {
      setSwipeOffset(-300);
      setTimeout(() => {
        onSwipeDelete(email);
      }, 200);
    } else if (swipeOffset > SWIPE_THRESHOLD_UNREAD) {
      setSwipeOffset(0);
      onMarkAsUnread(email);
    } else {
      setSwipeOffset(0);
    }
  };

  const handleMouseLeave = () => {
    if (isMouseDown.current) {
      isMouseDown.current = false;
      setIsSwiping(false);
      setSwipeOffset(0);
    }
  };

  return (
    <div className="relative overflow-hidden">
      {/* Mark as Unread Background (swipe right) */}
      <div 
        className={cn(
          "absolute inset-y-0 left-0 flex items-center justify-start px-4 bg-blue-500 transition-opacity",
          swipeOffset > 20 ? "opacity-100" : "opacity-0"
        )}
        style={{ width: swipeOffset > 0 ? swipeOffset + 20 : 0 }}
      >
        <Mail className="h-5 w-5 text-white" />
      </div>

      {/* Delete Background (swipe left) */}
      <div 
        className={cn(
          "absolute inset-y-0 right-0 flex items-center justify-end px-4 bg-destructive transition-opacity",
          swipeOffset < -20 ? "opacity-100" : "opacity-0"
        )}
        style={{ width: swipeOffset < 0 ? Math.abs(swipeOffset) + 20 : 0 }}
      >
        <Trash className="h-5 w-5 text-destructive-foreground" />
      </div>

      {/* Email Content */}
      <div
        onClick={() => !isSwiping && swipeOffset === 0 && onSelect(email)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        className={cn(
          "p-4 cursor-pointer transition-colors bg-background select-none border-l-4",
          isSelected 
            ? "bg-primary/10 border-l-primary" 
            : "hover:bg-accent/50 border-l-transparent",
          !email.is_read && "bg-blue-50 dark:bg-blue-950/40 border-l-blue-500"
        )}
        style={{ 
          transform: `translateX(${swipeOffset}px)`,
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src="" />
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {getInitials(email.from_address)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-0.5">
              <p className={cn(
                "text-sm truncate flex-1",
                !email.is_read && "font-semibold"
              )}>
                {getSenderName(email.from_address)}
              </p>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-transparent"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleStar(email, e);
                  }}
                >
                  <Star className={cn(
                    "h-4 w-4",
                    email.is_starred ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40"
                  )} />
                </Button>
              </div>
            </div>
            
            <div className="flex items-center gap-2 mb-1">
              <h3 className={cn(
                "text-sm truncate flex-1",
                !email.is_read && "font-medium"
              )}>
                {email.subject || "(Kein Betreff)"}
              </h3>
              <span className="text-xs text-muted-foreground shrink-0">
                {formatDate(email.received_at || email.sent_at)}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground truncate flex-1">
                {email.body_text 
                  ? formatEmailText(email.body_text).substring(0, 80) 
                  : email.body_html 
                    ? formatEmailText(email.body_html.replace(/<[^>]*>/g, '')).substring(0, 80)
                    : "—"}
              </p>
              {email.attachments && Array.isArray(email.attachments) && email.attachments.length > 0 && (
                <Paperclip className="h-3 w-3 text-muted-foreground shrink-0" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Profile {
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  organization_id?: string;
  phone?: string;
  email?: string;
  address?: string;
  contact_email?: string;
}

interface Organization {
  id: string;
  name: string;
  logo_url?: string;
  color?: string;
}

export const ModernEmailClient = ({ className }: ModernEmailClientProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [emails, setEmails] = useState<Email[]>([]);
  const [folderCounts, setFolderCounts] = useState<{
    inbox: number;
    inboxServer: number; // Actual count from IMAP server
    starred: number;
    sent: number;
    drafts: number;
    archive: number;
    trash: number;
  }>({ inbox: 0, inboxServer: 0, starred: 0, sent: 0, drafts: 0, archive: 0, trash: 0 });
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [isAppSidebarOpen, setIsAppSidebarOpen] = useState(false);
  const [isFolderSidebarOpen, setIsFolderSidebarOpen] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showComposeDialog, setShowComposeDialog] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  
  // Infinite scroll & server search states
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isServerSearching, setIsServerSearching] = useState(false);
  const [serverSearchResults, setServerSearchResults] = useState<Email[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const emailListRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [replyData, setReplyData] = useState<{
    to: string;
    subject: string;
    body?: string;
    originalHtml?: string;
    mode: 'reply' | 'replyAll' | 'forward';
    cc?: string;
    attachments?: any[];
    emailId?: string;
  } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{
    open: boolean;
    loading: boolean;
    data: string | null;
    contentType: string;
    filename: string;
  }>({ open: false, loading: false, data: null, contentType: '', filename: '' });

  const [saveAttachmentDialogOpen, setSaveAttachmentDialogOpen] = useState(false);
  const [attachmentToSave, setAttachmentToSave] = useState<{
    filename: string;
    contentType: string;
    size: number;
    partNumber: string;
    attachmentIndex?: number;
  } | null>(null);

  // Track which attachments are saved to which customers
  const [attachmentSaves, setAttachmentSaves] = useState<{
    [key: string]: { customerName: string; customerId: string };
  }>({});

  // Customer quick view dialog state
  const [customerQuickViewOpen, setCustomerQuickViewOpen] = useState(false);
  const [customerQuickViewId, setCustomerQuickViewId] = useState<string | null>(null);
  const [customerQuickViewName, setCustomerQuickViewName] = useState<string>("");

  // Email re-auth dialog state
  const [showReAuthDialog, setShowReAuthDialog] = useState(false);
  const [reAuthAccountId, setReAuthAccountId] = useState<string>("");
  const [reAuthEmailAddress, setReAuthEmailAddress] = useState<string>("");

  // Determine current folder from URL
  const currentFolder = (() => {
    const path = location.pathname;
    if (path.includes('/email/sent')) return 'sent';
    if (path.includes('/email/drafts')) return 'drafts';
    if (path.includes('/email/starred')) return 'starred';
    if (path.includes('/email/archive')) return 'archive';
    if (path.includes('/email/trash')) return 'trash';
    return 'inbox';
  })();

  const setCurrentFolder = (folder: string) => {
    navigate(`/email/${folder === 'inbox' ? 'inbox' : folder}`);
  };

  const formatCount = (count: number): string => {
    if (count >= 1000) {
      return count.toLocaleString('de-DE');
    }
    return count.toString();
  };

  const folders = [
    { id: "inbox", name: "Posteingang", icon: Inbox, count: folderCounts.inboxServer || folderCounts.inbox, showCount: true },
    { id: "starred", name: "Markiert", icon: Star, count: 0, showCount: false },
    { id: "sent", name: "Gesendet", icon: Send, count: 0, showCount: false },
    { id: "drafts", name: "Entwürfe", icon: FileText, count: 0, showCount: false },
    { id: "archive", name: "Archiv", icon: Archive, count: 0, showCount: false },
    { id: "trash", name: "Papierkorb", icon: Trash, count: 0, showCount: false },
  ];

  const initials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : 'U';

  useEffect(() => {
    if (user) {
      loadProfile();
      loadAccounts();
      loadEmails();
    }
  }, [user, currentFolder]);

  // Handle navigation from notification - auto-select email or show re-auth
  useEffect(() => {
    const state = location.state as { 
      selectedEmailId?: string;
      showReAuth?: boolean;
      reAuthAccountId?: string;
      reAuthEmailAddress?: string;
    } | null;
    
    // Handle re-auth request from notification
    if (state?.showReAuth && state?.reAuthAccountId && state?.reAuthEmailAddress) {
      setReAuthAccountId(state.reAuthAccountId);
      setReAuthEmailAddress(state.reAuthEmailAddress);
      setShowReAuthDialog(true);
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    
    if (state?.selectedEmailId && emails.length > 0) {
      const targetEmail = emails.find(e => e.id === state.selectedEmailId);
      if (targetEmail) {
        handleEmailSelect(targetEmail);
        // Clear the navigation state to prevent re-opening on refresh
        navigate(location.pathname, { replace: true, state: {} });
      } else {
        // Email not in current list - try to load it directly
        loadAndSelectEmail(state.selectedEmailId);
      }
    }
  }, [location.state, emails]);

  const loadAndSelectEmail = async (emailId: string) => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('emails')
        .select('*')
        .eq('id', emailId)
        .eq('user_id', user.id)
        .single();
      
      if (data && !error) {
        setSelectedEmail(data);
        // Fetch the body
        fetchEmailBody(data);
        // Clear navigation state
        navigate(location.pathname, { replace: true, state: {} });
      }
    } catch (err) {
      console.error('Error loading email from notification:', err);
    }
  };

  // Load folder counts when accounts are loaded (for server count)
  useEffect(() => {
    if (accounts.length > 0) {
      loadFolderCounts();
    }
  }, [accounts]);

  // Separate effect for realtime subscription
  useEffect(() => {
    if (!user) return;

    console.log('Setting up realtime subscription for emails...');
    
    const channel = supabase
      .channel('email-updates-' + user.id)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New email received:', payload);
          // Add new email to the list
          const newEmail = payload.new as any;
          setEmails(prev => {
            // Check if email already exists
            if (prev.some(e => e.id === newEmail.id)) return prev;
            
            const transformedEmail = {
              ...newEmail,
              to_addresses: Array.isArray(newEmail.to_addresses) ? newEmail.to_addresses : 
                           typeof newEmail.to_addresses === 'string' ? [newEmail.to_addresses] : [],
              attachments: newEmail.attachments || [],
              labels: newEmail.labels || []
            };
            
            // Add to beginning if inbox, sorted by date
            return [transformedEmail, ...prev].sort((a, b) => 
              new Date(b.received_at || b.sent_at).getTime() - new Date(a.received_at || a.sent_at).getTime()
            );
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'emails',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📝 Email updated via Realtime:', payload.new);
          const updatedEmail = payload.new as any;
          
          const transformedEmail = {
            ...updatedEmail,
            to_addresses: Array.isArray(updatedEmail.to_addresses) ? updatedEmail.to_addresses : 
                         typeof updatedEmail.to_addresses === 'string' ? [updatedEmail.to_addresses] : [],
            attachments: updatedEmail.attachments || [],
            labels: updatedEmail.labels || []
          };
          
          // Update emails list
          setEmails(prev => prev.map(e => 
            e.id === updatedEmail.id ? transformedEmail : e
          ));
          
          // Also update selectedEmail if it's the same email
          setSelectedEmail(prev => 
            prev?.id === updatedEmail.id ? transformedEmail : prev
          );
        }
      )
      .subscribe((status) => {
        console.log('Realtime subscription status:', status);
      });

    return () => {
      console.log('Cleaning up realtime subscription...');
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Note: Auto-sync is now handled globally in useBackgroundEmailSync hook (App.tsx)
  const loadProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('first_name, last_name, avatar_url, organization_id, phone, email, address, contact_email')
      .eq('user_id', user.id)
      .single();
    if (data) {
      setProfile(data);
      
      // Load organization data
      if (data.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('id, name, logo_url, color')
          .eq('id', data.organization_id)
          .single();
        if (orgData) setOrganization(orgData);
      }
    }
  };

  const loadAccounts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('email_accounts')
        .select('id, email_address, display_name, is_active')
        .eq('user_id', user.id);

      if (error) throw error;

      setAccounts(data || []);
      
      // Zeige Setup-Dialog wenn keine Accounts vorhanden
      if (!data || data.length === 0) {
        setShowSetupDialog(true);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadFolderCounts = async () => {
    if (!user) return;

    try {
      // Load counts for all folders in parallel
      const [inboxResult, starredResult, sentResult, draftsResult, archiveResult, trashResult] = await Promise.all([
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'received').eq('is_read', false),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('is_starred', true),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'sent'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'draft'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'archived'),
        supabase.from('emails').select('id', { count: 'exact', head: true })
          .eq('user_id', user.id).eq('status', 'deleted'),
      ]);

      // Get active account to fetch server count
      const activeAccount = accounts.find(a => a.is_active) || accounts[0];
      let serverCount = 0;
      
      if (activeAccount) {
        try {
          const { data: serverData } = await supabase.functions.invoke('get-email-count', {
            body: { accountId: activeAccount.id }
          });
          if (serverData?.total_emails) {
            serverCount = serverData.total_emails;
          }
        } catch (err) {
          console.error('Error fetching server email count:', err);
        }
      }

      setFolderCounts({
        inbox: inboxResult.count || 0,
        inboxServer: serverCount,
        starred: starredResult.count || 0,
        sent: sentResult.count || 0,
        drafts: draftsResult.count || 0,
        archive: archiveResult.count || 0,
        trash: trashResult.count || 0,
      });
    } catch (error) {
      console.error('Error loading folder counts:', error);
    }
  };

  const loadEmails = async (showLoading = true) => {
    if (!user) return;

    try {
      // Only show loading spinner on initial load, not on background refreshes
      if (showLoading && emails.length === 0) {
        setLoading(true);
      }
      
      // Always load folder counts
      loadFolderCounts();
      
      let query = supabase
        .from('emails')
        .select('*')
        .eq('user_id', user.id);

      if (currentFolder === "starred") {
        query = query.eq('is_starred', true).order('received_at', { ascending: false, nullsFirst: false });
      } else if (currentFolder === "sent") {
        query = query.eq('status', 'sent').order('sent_at', { ascending: false, nullsFirst: false });
      } else if (currentFolder === "inbox") {
        query = query.eq('status', 'received').order('received_at', { ascending: false });
      } else if (currentFolder === "drafts") {
        query = query.eq('status', 'draft').order('created_at', { ascending: false });
      } else if (currentFolder === "archive") {
        query = query.eq('status', 'archived').order('received_at', { ascending: false });
      } else if (currentFolder === "trash") {
        query = query.eq('status', 'deleted').order('received_at', { ascending: false });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query.limit(100);

      if (error) throw error;

      const transformedEmails = (data || []).map(email => ({
        ...email,
        to_addresses: Array.isArray(email.to_addresses) ? email.to_addresses : 
                     typeof email.to_addresses === 'string' ? [email.to_addresses] : [],
        attachments: email.attachments || [],
        labels: email.labels || []
      }));

      setEmails(transformedEmails);
      setCurrentOffset(transformedEmails.length);
      // Inbox/Sent: allow infinite scroll to pull older emails from IMAP even if DB currently has fewer rows
      setHasMoreEmails(
        currentFolder === "inbox" || currentFolder === "sent"
          ? true
          : transformedEmails.length >= 100
      );
    } catch (error) {
      console.error('Error loading emails:', error);
      if (showLoading) {
        toast({
          title: "Fehler",
          description: "E-Mails konnten nicht geladen werden.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Load more emails from IMAP server (infinite scroll)
  const loadMoreEmails = async () => {
    if (loadingMore || !hasMoreEmails || !user || searchQuery.trim()) return;
    
    const activeAccount = accounts.find(a => a.is_active) || accounts[0];
    if (!activeAccount) return;

    setLoadingMore(true);
    try {
      console.log(`📧 Lade weitere E-Mails... Offset: ${currentOffset}`);
      
      const { data, error } = await supabase.functions.invoke('load-emails-live', {
        body: { 
          accountId: activeAccount.id,
          folder: currentFolder,
          limit: 50,
          offset: currentOffset,
        }
      });

      if (error) throw error;

      const fetchedCount = Array.isArray(data?.emails) ? data.emails.length : 0;

      if (fetchedCount > 0) {
        const newEmails: Email[] = data.emails.map((email: any) => ({
          ...email,
          to_addresses: Array.isArray(email.to_addresses) ? email.to_addresses : [],
          attachments: email.attachments || [],
          labels: email.labels || [],
          is_live_loaded: true,
        }));

        setEmails((prev) => {
          const existingKeys = new Set(prev.map((e) => e.message_id || e.id));
          const uniqueNew = newEmails.filter((e) => !existingKeys.has(e.message_id || e.id));
          return [...prev, ...uniqueNew];
        });

        // Important: offset should move by what we fetched from server (not by uniqueNew), otherwise we can get stuck
        setCurrentOffset((prev) => prev + fetchedCount);
        setHasMoreEmails(Boolean(data?.hasMore));
      } else {
        setHasMoreEmails(false);
      }
    } catch (error) {
      console.error('Fehler beim Laden weiterer E-Mails:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Server-side IMAP search
  const searchOnServer = async (query: string) => {
    if (!query.trim() || query.trim().length < 2) {
      setServerSearchResults([]);
      setIsServerSearching(false);
      return;
    }

    const activeAccount = accounts.find(a => a.is_active) || accounts[0];
    if (!activeAccount) return;

    setIsServerSearching(true);
    try {
      console.log(`🔍 Server-Suche: "${query}"`);
      
      const { data, error } = await supabase.functions.invoke('search-emails-imap', {
        body: { 
          accountId: activeAccount.id,
          searchQuery: query,
          limit: 50
        }
      });

      if (error) throw error;

      if (data?.emails) {
        setServerSearchResults(data.emails.map((email: any) => ({
          ...email,
          to_addresses: Array.isArray(email.to_addresses) ? email.to_addresses : [],
          attachments: email.attachments || [],
          labels: email.labels || [],
          is_server_search: true
        })));
      }
    } catch (error) {
      console.error('Server-Suche Fehler:', error);
    } finally {
      setIsServerSearching(false);
    }
  };

  // Debounced server search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchOnServer(searchQuery);
      }, 500);
    } else {
      setServerSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, accounts]);

  // Handle scroll for infinite loading
  const handleEmailListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Calculate how far from the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // Trigger load more when near bottom (within 300px)
    if (distanceFromBottom < 300 && !loadingMore && hasMoreEmails && !searchQuery.trim()) {
      console.log('📜 Scroll near bottom, loading more...', { distanceFromBottom, scrollHeight, scrollTop, clientHeight });
      loadMoreEmails();
    }
  };

  const syncEmails = async () => {
    if (!user) return;

    setRefreshing(true);
    try {
      const { data: activeAccounts } = await supabase
        .from('email_accounts')
        .select('id, email_address')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (!activeAccounts || activeAccounts.length === 0) {
        setShowSetupDialog(true);
        return;
      }

      for (const account of activeAccounts) {
        const { data, error } = await supabase.functions.invoke('sync-emails', {
          body: { accountId: account.id }
        });
        
        // Check for authentication failure
        if (data?.code === 'AUTH_FAILED') {
          console.error(`Auth-Fehler für ${account.email_address}:`, data.message);
          setReAuthAccountId(account.id);
          setReAuthEmailAddress(account.email_address);
          setShowReAuthDialog(true);
          toast({
            title: "E-Mail-Anmeldung erforderlich",
            description: `Das Passwort für ${account.email_address} ist ungültig oder abgelaufen.`,
            variant: "destructive",
          });
          return; // Stop syncing other accounts
        }
      }

      // Reload emails without showing loading spinner (realtime will also update)
      loadEmails(false);
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Synchronisierung fehlgeschlagen",
        description: "E-Mails konnten nicht synchronisiert werden.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEmailBody = async (email: Email) => {
    // Always try to fetch if we don't have valid content
    
    setLoadingBody(true);
    try {
      // For temporary IMAP IDs (search results or live-loaded), we need to pass messageId and accountId
      const isTemporaryImapId = email.id.startsWith('imap_search_') || 
                                 email.id.startsWith('imap_live_') || 
                                 email.id.startsWith('imap_');
      const requestBody: any = { emailId: email.id };
      
      if (isTemporaryImapId) {
        requestBody.messageId = (email as any).message_id;
        requestBody.accountId = email.account_id;
      }
      
      const { data, error } = await supabase.functions.invoke('fetch-email-body', {
        body: requestBody
      });

      if (!error && data) {
        // Check if email was not found on server - auto-remove it
        if (data.notFoundOnServer) {
          console.log('📭 Email not found on IMAP server - auto-removing from DB');
          
          // Delete from database
          await supabase
            .from('emails')
            .delete()
            .eq('id', email.id);
          
          // Remove from UI
          setEmails(prev => prev.filter(e => e.id !== email.id));
          setSelectedEmail(null);
          
          toast({
            title: "E-Mail nicht mehr verfügbar",
            description: "Diese E-Mail wurde vom Server gelöscht und aus der Liste entfernt.",
          });
          return;
        }
        
        // Update with body AND attachments
        const updatedEmail = {
          ...email,
          body_html: data.body_html || email.body_html,
          body_text: data.body_text || email.body_text,
          attachments: data.attachments || email.attachments
        };
        
        setSelectedEmail(prev => prev?.id === email.id ? updatedEmail : prev);
        
        // Update emails list
        setEmails(prev => prev.map(e => 
          e.id === email.id ? updatedEmail : e
        ));
      }
    } catch (error) {
      console.error('Error fetching email body:', error);
    } finally {
      setLoadingBody(false);
    }
  };

  const markAsRead = async (email: Email) => {
    if (email.is_read) return;

    try {
      // Update local state immediately
      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_read: true } : e)
      );

      // Get active account for IMAP sync
      const activeAccount = accounts.find(a => a.is_active) || accounts[0];
      
      // Call edge function to mark on IMAP server
      const { error } = await supabase.functions.invoke('mark-email-read-imap', {
        body: {
          emailId: email.id,
          messageId: (email as any).message_id,
          accountId: activeAccount?.id
        }
      });

      if (error) {
        console.error('Error marking as read on IMAP:', error);
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsUnread = async (email: Email) => {
    if (!email.is_read) return;

    console.log('📬 Marking email as unread:', email.id, email.subject);

    // Optimistic update for both emails list and selectedEmail
    setEmails(prev => 
      prev.map(e => e.id === email.id ? { ...e, is_read: false } : e)
    );
    setSelectedEmail(prev => 
      prev?.id === email.id ? { ...prev, is_read: false } : prev
    );

    try {
      // Get active account for IMAP sync
      const account = accounts.find(a => a.is_active) || accounts[0];
      
      const { error } = await supabase.functions.invoke('mark-email-unread-imap', {
        body: {
          emailId: email.id,
          messageId: email.message_id || '',
          accountId: email.account_id || account?.id || ''
        }
      });

      if (error) throw error;

      toast({
        title: "Als ungelesen markiert",
        description: "Die E-Mail wurde als ungelesen markiert.",
      });
    } catch (error) {
      console.error('Error marking as unread:', error);
      // Revert optimistic update on error
      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_read: true } : e)
      );
      toast({
        title: "Fehler",
        description: "E-Mail konnte nicht als ungelesen markiert werden.",
        variant: "destructive"
      });
    }
  };

  const openAttachment = async (attachment: any, index: number) => {
    if (!selectedEmail) return;
    
    setAttachmentPreview({
      open: true,
      loading: true,
      data: null,
      contentType: attachment.contentType || '',
      filename: attachment.filename || `Anhang ${index + 1}`
    });

    try {
      const { data, error } = await supabase.functions.invoke('fetch-email-attachment', {
        body: {
          emailId: selectedEmail.id,
          attachmentIndex: index,
          filename: attachment.filename || `attachment_${index}`
        }
      });

      if (error) throw error;

      if (data?.success && data.data) {
        // Clean base64 data
        const cleanData = data.data.replace(/[\r\n\s]/g, '');
        
        setAttachmentPreview(prev => ({
          ...prev,
          loading: false,
          data: cleanData,
          contentType: data.contentType || attachment.contentType || 'application/octet-stream',
          filename: data.filename || attachment.filename
        }));
      } else {
        throw new Error('Keine Daten empfangen');
      }
    } catch (error: any) {
      console.error('Attachment fetch error:', error);
      setAttachmentPreview(prev => ({ ...prev, loading: false }));
      toast({
        title: "Fehler",
        description: "Anhang konnte nicht geladen werden.",
        variant: "destructive"
      });
    }
  };

  const openSaveAttachmentDialog = (attachment: any, index: number, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!selectedEmail) return;

    setAttachmentToSave({
      filename: attachment.filename || `Anhang ${index + 1}`,
      contentType: attachment.contentType || attachment.content_type || 'application/octet-stream',
      size: attachment.size || 0,
      partNumber: attachment.partNumber || attachment.part_number || String(index + 1),
      attachmentIndex: index,
    });
    setSaveAttachmentDialogOpen(true);
  };

  const downloadAttachment = () => {
    if (!attachmentPreview.data) return;
    
    try {
      // Decode base64 to binary
      const binaryString = atob(attachmentPreview.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: attachmentPreview.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachmentPreview.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Fehler",
        description: "Download fehlgeschlagen.",
        variant: "destructive"
      });
    }
  };

  const toggleStarred = async (email: Email, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    
    try {
      const { error } = await supabase
        .from('emails')
        .update({ is_starred: !email.is_starred })
        .eq('id', email.id);

      if (error) throw error;

      setEmails(prev => 
        prev.map(e => e.id === email.id ? { ...e, is_starred: !e.is_starred } : e)
      );
      
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(prev => prev ? { ...prev, is_starred: !prev.is_starred } : null);
      }
    } catch (error) {
      console.error('Error toggling starred:', error);
    }
  };

  // State for undo functionality
  const [deletedEmailForUndo, setDeletedEmailForUndo] = useState<Email | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSwipeDelete = async (email: Email) => {
    // Store email for potential undo
    const emailToDelete = { ...email };
    setDeletedEmailForUndo(emailToDelete);

    // Clear any existing undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
    }

    // Optimistic UI update - remove email immediately
    setEmails(prev => prev.filter(e => e.id !== email.id));
    if (selectedEmail?.id === email.id) {
      setSelectedEmail(null);
    }

    // Show toast with undo option
    toast({
      title: "E-Mail gelöscht",
      description: "Die E-Mail wird vom Server entfernt.",
      action: (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleUndoDelete(emailToDelete)}
        >
          Rückgängig
        </Button>
      ),
    });

    // Set timeout to actually delete from server after 5 seconds
    undoTimeoutRef.current = setTimeout(async () => {
      try {
        // Get account ID for this email
        const accountId = accounts.find(a => a.is_active)?.id || accounts[0]?.id;
        
        // Delete from IMAP and database
        const { error } = await supabase.functions.invoke('delete-email-imap', {
          body: {
            emailId: email.id,
            messageId: (email as any).message_id,
            accountId
          }
        });

        if (error) {
          console.error('Error deleting email from IMAP:', error);
        }

        setDeletedEmailForUndo(null);
      } catch (err) {
        console.error('Failed to delete email:', err);
      }
    }, 5000);
  };

  const handleUndoDelete = async (email: Email) => {
    // Clear the delete timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }

    // Add email back to list
    setEmails(prev => {
      if (prev.some(e => e.id === email.id)) return prev;
      return [email, ...prev].sort((a, b) => 
        new Date(b.received_at || b.sent_at || '').getTime() - 
        new Date(a.received_at || a.sent_at || '').getTime()
      );
    });

    setDeletedEmailForUndo(null);

    toast({
      title: "Wiederhergestellt",
      description: "E-Mail wurde wiederhergestellt.",
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    if (isToday(date)) {
      return format(date, 'HH:mm', { locale: de });
    } else if (isYesterday(date)) {
      return 'Gestern';
    } else {
      return format(date, 'dd.MM.yy', { locale: de });
    }
  };

  const getInitials = (emailAddress: string) => {
    const { name } = extractDisplayName(emailAddress);
    if (!name) return '??';
    const parts = name.split(/[\s._-]+/);
    return parts.map(part => part.charAt(0).toUpperCase()).join('').slice(0, 2);
  };

  const getSenderName = (emailAddress: string) => {
    return extractDisplayName(emailAddress).name;
  };

  const getSenderEmail = (emailAddress: string) => {
    return extractDisplayName(emailAddress).email;
  };

  // Combine local filtered emails with server search results
  const localFilteredEmails = emails.filter(email =>
    email.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    email.from_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Use server results when searching, otherwise use local
  const displayEmails = searchQuery.trim().length >= 2 && serverSearchResults.length > 0
    ? [...serverSearchResults, ...localFilteredEmails.filter(e => 
        !serverSearchResults.some(sr => sr.message_id === e.message_id)
      )]
    : localFilteredEmails;

  const filteredEmails = displayEmails;

  const handleEmailSelect = async (email: Email) => {
    setSelectedEmail(email);
    markAsRead(email);
    setIsFolderSidebarOpen(false);
    
    // Load attachment saves for this email
    loadAttachmentSaves(email.id);
    
    // Fetch body if not present or if existing body looks like incomplete MIME data
    const hasValidBody = (email.body_html && email.body_html.trim().length > 50) ||
      (email.body_text && email.body_text.trim().length > 50 && 
       !email.body_text.includes('Content-Type:') && 
       !email.body_text.includes('Content-Transfer-Encoding:'));
    
    if (!hasValidBody) {
      await fetchEmailBody(email);
    }
  };

  const loadAttachmentSaves = async (emailId: string) => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('email_attachment_saves')
        .select(`
          attachment_filename,
          customer_id,
          customers (
            first_name,
            last_name
          )
        `)
        .eq('email_id', emailId)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error loading attachment saves:', error);
        return;
      }
      
      const savesMap: { [key: string]: { customerName: string; customerId: string } } = {};
      for (const save of data || []) {
        const customer = save.customers as any;
        if (customer) {
          const customerName = `${customer.first_name} ${customer.last_name || ''}`.trim();
          savesMap[save.attachment_filename] = {
            customerName,
            customerId: save.customer_id
          };
        }
      }
      
      setAttachmentSaves(savesMap);
    } catch (error) {
      console.error('Error loading attachment saves:', error);
    }
  };

  const renderEmailContent = () => {
    if (!selectedEmail) return null;
    
    const bodyHtml = selectedEmail.body_html;
    const bodyText = selectedEmail.body_text;
    
    if (loadingBody) {
      return (
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">E-Mail wird geladen...</span>
        </div>
      );
    }
    
    // Helper to check if content is just MIME headers/junk
    const isJunkContent = (content: string | null | undefined): boolean => {
      if (!content) return true;
      const trimmed = content.trim();
      if (trimmed.length < 20) return true;
      
      // Check if it's mostly MIME headers
      const mimePatterns = /^(Content-Type:|Content-Transfer-Encoding:|MIME-Version:|boundary=|--[_A-Za-z0-9=.-]+)/mi;
      const lines = trimmed.split('\n').filter(l => l.trim());
      const headerLines = lines.filter(l => mimePatterns.test(l.trim()));
      
      // If more than 50% of lines are MIME headers, it's junk
      if (headerLines.length > lines.length * 0.5) return true;
      
      // If it's very short and starts with MIME header, it's junk
      if (trimmed.length < 100 && mimePatterns.test(trimmed)) return true;
      
      return false;
    };
    
    // Try multiple sources for HTML content
    let htmlCandidate: string | null = null;
    
    // 1. Check body_html - but verify it actually contains HTML
    if (bodyHtml && bodyHtml.trim().length > 0 && !isJunkContent(bodyHtml)) {
      const decodedHtml = maybeDecodeBase64(bodyHtml);
      if (/<\/?[a-z][^>]*>/i.test(decodedHtml)) {
        htmlCandidate = decodedHtml;
      }
    }
    
    // 2. Check body_text for embedded HTML (common with MIME emails)
    if (!htmlCandidate && bodyText && !isJunkContent(bodyText)) {
      const fromMime = extractMimeSection(bodyText, "text/html");
      if (fromMime && !isJunkContent(fromMime)) {
        htmlCandidate = fromMime;
      } else {
        const fromRaw = extractHtmlSegmentFromRaw(bodyText);
        if (fromRaw && !isJunkContent(fromRaw)) {
          htmlCandidate = fromRaw;
        } else {
          // Try base64 decode on bodyText
          const decoded = maybeDecodeBase64(bodyText);
          if (decoded !== bodyText && /<\/?[a-z][^>]*>/i.test(decoded) && !isJunkContent(decoded)) {
            htmlCandidate = decoded;
          }
        }
      }
    }
    
    // 3. If still no HTML but bodyText looks like it contains HTML fragments
    if (!htmlCandidate && bodyText && !isJunkContent(bodyText) && looksLikeHtmlEmail(bodyText)) {
      htmlCandidate = bodyText;
    }

    if (htmlCandidate) {
      const cleanHtml = formatEmailHtml(htmlCandidate);
      
      // Verify we actually have content after cleaning
      if (cleanHtml.trim().length > 20) {
        const baseResponsiveCss = `
          <style>
            img { max-width: 100% !important; height: auto !important; }
            table { max-width: 100% !important; }
            a { color: #2563eb; }
          </style>
        `;

        const iframeContent = buildEmailIframeContent(cleanHtml, baseResponsiveCss);

        return (
          <iframe
            srcDoc={iframeContent}
            className="w-full border-0 bg-background rounded-md"
            style={{
              minHeight: '400px',
              height: 'auto',
            }}
            sandbox="allow-same-origin"
            onLoad={(e) => {
              const iframe = e.target as HTMLIFrameElement;
              const doc = iframe.contentDocument;
              if (!doc) return;

              // Clear any previous resize timeouts
              try {
                const prevTimeouts: number[] = (iframe as any).__emailResizeTimeouts ?? [];
                prevTimeouts.forEach((t) => window.clearTimeout(t));
              } catch {}

              const resize = () => {
                try {
                  const bodyH = doc.body?.scrollHeight ?? 0;
                  const htmlH = doc.documentElement?.scrollHeight ?? 0;
                  const contentHeight = Math.max(bodyH, htmlH, 400);
                  iframe.style.height = contentHeight + 32 + "px";
                } catch {}
              };

              // Initial + delayed passes (images/fonts/layout)
              resize();
              try {
                (iframe as any).__emailResizeTimeouts = [
                  50,
                  200,
                  800,
                  1500,
                ].map((ms) => window.setTimeout(resize, ms));

                Array.from(doc.images || []).forEach((img) => {
                  img.addEventListener("load", resize, { once: true });
                  img.addEventListener("error", resize, { once: true });
                });
              } catch {}
            }}
          />
        );
      }
    }

    // Try to get plain text content
    if (bodyText && !isJunkContent(bodyText)) {
      const textCandidate = extractMimeSection(bodyText, "text/plain") ?? bodyText;
      const formatted = formatEmailText(textCandidate);
      
      if (formatted.trim().length > 10) {
        return (
          <div className="whitespace-pre-wrap text-sm font-sans leading-relaxed">
            {linkifyText(formatted)}
          </div>
        );
      }
    }
    
    
    // No valid content - show reload button
    return (
      <div className="text-muted-foreground text-center py-8">
        <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Kein E-Mail-Inhalt verfügbar</p>
        <Button 
          variant="outline" 
          size="sm" 
          className="mt-4"
          onClick={() => fetchEmailBody(selectedEmail)}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Erneut laden
        </Button>
      </div>
    );
  };

  // Folder Sidebar Component
  const FolderSidebar = () => (
    <div className="h-full bg-card/50 flex flex-col">
      <div className="p-4 border-b">
        <Button 
          className="w-full justify-start gap-2" 
          size="sm"
          onClick={() => setShowComposeDialog(true)}
        >
          <Edit className="h-4 w-4" />
          Neue E-Mail
        </Button>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {folders.map((folder) => {
            const Icon = folder.icon;
            return (
              <Button
                key={folder.id}
                variant={currentFolder === folder.id ? "secondary" : "ghost"}
                className="w-full h-10 px-3"
                onClick={() => {
                  setCurrentFolder(folder.id);
                  setIsFolderSidebarOpen(false);
                }}
              >
                <div className="flex items-center justify-between w-full gap-2">
                  <div className="flex flex-1 items-center gap-3 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 min-w-0 truncate text-left">{folder.name}</span>
                  </div>

                  {folder.showCount && folder.count > 0 && (
                    <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                      {formatCount(folder.count)}
                    </span>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </ScrollArea>
      
      <div className="p-4 border-t">
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={() => setShowSettings(true)}
        >
          <Settings className="h-4 w-4" />
          Einstellungen
        </Button>
      </div>
    </div>
  );

  return (
    <div className={cn("h-screen flex flex-col bg-background w-full overflow-hidden", className)}>
      {/* App Sidebar as Sheet Overlay */}
      <Sheet open={isAppSidebarOpen} onOpenChange={setIsAppSidebarOpen}>
        <SheetContent 
          side="left" 
          className="!p-0 overflow-y-auto bg-sidebar text-sidebar-foreground border-sidebar-border" 
          style={{ width: '16rem', maxWidth: '16rem' }}
          hideCloseButton
        >
          <VisuallyHidden>
            <span>Navigation</span>
          </VisuallyHidden>
          <AppSidebarContent onNavigate={() => setIsAppSidebarOpen(false)} hideActiveState />
        </SheetContent>
      </Sheet>

      {/* Email Setup Dialog */}
      <EmailSetupDialog 
        open={showSetupDialog} 
        onOpenChange={setShowSetupDialog}
        onSuccess={() => {
          loadAccounts();
          syncEmails();
        }}
      />

      {/* Email Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-6xl w-[min(96vw,72rem)] max-h-[90vh] overflow-auto p-0">
          <EmailSettings onClose={() => setShowSettings(false)} />
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="h-14 bg-primary flex items-center px-3 md:px-4 gap-2 md:gap-4 shrink-0 sticky top-0 z-50">
        {/* Left Section */}
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsAppSidebarOpen(true)}
            className="text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* Mobile Folder Sidebar Toggle */}
          <Sheet open={isFolderSidebarOpen} onOpenChange={setIsFolderSidebarOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
              >
                <Mail className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="!p-0 w-64" hideCloseButton>
              <VisuallyHidden>
                <span>E-Mail-Ordner</span>
              </VisuallyHidden>
              <FolderSidebar />
            </SheetContent>
          </Sheet>

          {/* Title */}
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary-foreground hidden md:block" />
            <h1 className="text-lg font-semibold text-primary-foreground">Mail</h1>
          </div>
        </div>

        {/* Center - Search */}
        <div className="flex-1 max-w-xl mx-auto px-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-primary-foreground/50" />
            <Input
              placeholder="E-Mails durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-8 bg-primary-foreground/10 border-0 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-1 focus-visible:ring-primary-foreground/30 h-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-primary-foreground/20 text-primary-foreground/50 hover:text-primary-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1 md:gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={syncEmails}
            disabled={refreshing}
            className="text-primary-foreground/90 hover:text-primary-foreground hover:bg-primary-foreground/10 h-9 w-9"
          >
            <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
          </Button>
          
          <Avatar className="h-8 w-8 border border-primary-foreground/20">
            <AvatarImage src={profile?.avatar_url || ''} />
            <AvatarFallback className="bg-primary-foreground/10 text-primary-foreground text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Folder Sidebar - Desktop */}
        <div className="hidden lg:flex lg:flex-col w-56 border-r shrink-0 h-full overflow-hidden">
          <FolderSidebar />
        </div>

        {/* Email List */}
        <div className={cn(
          "w-full lg:w-80 xl:w-96 border-r bg-card/30 shrink-0 flex flex-col h-full overflow-hidden",
          selectedEmail ? "hidden lg:flex" : "flex"
        )}>
          <ScrollArea 
            className="flex-1 h-full" 
            viewportRef={scrollViewportRef}
            onViewportScroll={handleEmailListScroll}
          >
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
                <p className="text-sm text-muted-foreground mt-3">E-Mails werden geladen...</p>
              </div>
            ) : filteredEmails.length === 0 && !isServerSearching ? (
              <div className="p-8 text-center">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground font-medium">
                  {searchQuery.trim() ? "Keine Treffer" : "Keine E-Mails"}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {searchQuery.trim() 
                    ? "Versuchen Sie einen anderen Suchbegriff"
                    : accounts.length === 0 
                      ? "Richten Sie ein E-Mail-Konto ein" 
                      : "Klicken Sie auf Aktualisieren, um E-Mails zu laden"}
                </p>
                {accounts.length === 0 && !searchQuery.trim() && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => setShowSetupDialog(true)}
                  >
                    E-Mail-Konto einrichten
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {/* Server search indicator */}
                {isServerSearching && (
                  <div className="p-3 bg-muted/30 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Suche auf Server...
                  </div>
                )}
                
                {/* Server search results header */}
                {searchQuery.trim().length >= 2 && serverSearchResults.length > 0 && (
                  <div className="p-2 bg-primary/5 text-xs text-muted-foreground font-medium">
                    {serverSearchResults.length} Treffer vom Server
                  </div>
                )}
                
                {filteredEmails.map((email) => (
                  <SwipeableEmailItem
                    key={email.id}
                    email={email}
                    isSelected={selectedEmail?.id === email.id}
                    onSelect={handleEmailSelect}
                    onToggleStar={toggleStarred}
                    onSwipeDelete={handleSwipeDelete}
                    onMarkAsUnread={markAsUnread}
                    getInitials={getInitials}
                    getSenderName={getSenderName}
                    formatDate={formatDate}
                    formatEmailText={formatEmailText}
                  />
                ))}
                
                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="p-4 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-primary" />
                    <p className="text-xs text-muted-foreground mt-2">Lade weitere E-Mails...</p>
                  </div>
                )}
                
                {/* End of list indicator */}
                {!hasMoreEmails && !searchQuery.trim() && filteredEmails.length > 0 && (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    Alle E-Mails geladen
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Email Content */}
        <div className={cn(
          "flex-1 bg-background flex flex-col h-full overflow-hidden",
          !selectedEmail ? "hidden lg:flex" : "flex"
        )}>
          {selectedEmail ? (
            <div className="flex flex-col h-full w-full overflow-hidden">
              {/* Email Header */}
              <div className="shrink-0 border-b bg-card/50 p-4 md:p-6">
                {/* Back Button - Mobile */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedEmail(null)}
                  className="lg:hidden mb-3 -ml-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Zurück
                </Button>
                
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg md:text-xl font-semibold mb-3 break-words">
                      {selectedEmail.subject || "(Kein Betreff)"}
                    </h1>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className="bg-primary/10 text-primary text-sm">
                          {getInitials(selectedEmail.from_address)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {getSenderName(selectedEmail.from_address)}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {getSenderEmail(selectedEmail.from_address)}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">
                        {formatDate(selectedEmail.received_at || selectedEmail.sent_at)}
                      </span>
                    </div>
                  </div>
                  
                </div>
                
                {Array.isArray(selectedEmail.to_addresses) && selectedEmail.to_addresses.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">An:</span> {selectedEmail.to_addresses.join(', ')}
                  </div>
                )}

                <div className="flex items-center gap-1 shrink-0 mt-2 justify-end">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      // Bei gesendeten E-Mails antworten wir an die Empfänger, nicht an uns selbst
                      const isSentEmail = selectedEmail.status === 'sent';
                      const replyTo = isSentEmail 
                        ? (Array.isArray(selectedEmail.to_addresses) ? selectedEmail.to_addresses[0] : '')
                        : getSenderEmail(selectedEmail.from_address);
                      
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
                      
                      // Body-Text bereinigen (Quoted-Printable/HTML/Encoding)
                      const cleanBodyText = formatEmailText(
                        selectedEmail.body_text || selectedEmail.body_html || ''
                      );
                      
                      const quotedBody = `\n\n\n-------- Ursprüngliche Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${cleanBodyText}`;
                      
                      setReplyData({
                        to: replyTo,
                        subject: replySubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'reply',
                        attachments: selectedEmail.attachments || undefined,
                        emailId: selectedEmail.id
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Antworten"
                  >
                    <Reply className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      // Bei gesendeten E-Mails antworten wir an die Empfänger, nicht an uns selbst
                      const isSentEmail = selectedEmail.status === 'sent';
                      const replyTo = isSentEmail 
                        ? (Array.isArray(selectedEmail.to_addresses) ? selectedEmail.to_addresses[0] : '')
                        : getSenderEmail(selectedEmail.from_address);
                      
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const replySubject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
                      const ccAddresses = Array.isArray(selectedEmail.to_addresses) 
                        ? selectedEmail.to_addresses.filter((addr: string) => addr !== accounts[0]?.email_address && addr !== replyTo).join(', ')
                        : '';
                      
                      // Body-Text bereinigen (Quoted-Printable/HTML/Encoding)
                      const cleanBodyText = formatEmailText(
                        selectedEmail.body_text || selectedEmail.body_html || ''
                      );
                      
                      const quotedBody = `\n\n\n-------- Ursprüngliche Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${cleanBodyText}`;
                      
                      setReplyData({
                        to: replyTo,
                        subject: replySubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'replyAll',
                        cc: ccAddresses,
                        attachments: selectedEmail.attachments || undefined,
                        emailId: selectedEmail.id
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Allen antworten"
                  >
                    <ReplyAll className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      const originalSubject = decodeEmailField(selectedEmail.subject || '');
                      const fwdSubject = originalSubject.startsWith('Fwd:') ? originalSubject : `Fwd: ${originalSubject}`;
                      
                      // Body-Text bereinigen (Quoted-Printable/HTML/Encoding)
                      const cleanBodyText = formatEmailText(
                        selectedEmail.body_text || selectedEmail.body_html || ''
                      );
                      
                      const quotedBody = `\n\n\n-------- Weitergeleitete Nachricht --------\nVon: ${selectedEmail.from_address}\nDatum: ${formatDate(selectedEmail.received_at || selectedEmail.sent_at)}\nBetreff: ${originalSubject}\n\n${cleanBodyText}`;
                      
                      setReplyData({
                        to: '',
                        subject: fwdSubject,
                        body: quotedBody,
                        originalHtml: selectedEmail.body_html || undefined,
                        mode: 'forward',
                        attachments: selectedEmail.attachments || undefined,
                        emailId: selectedEmail.id
                      });
                      setShowComposeDialog(true);
                    }}
                    title="Weiterleiten"
                  >
                    <Forward className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={(e) => toggleStarred(selectedEmail, e)}
                    title={selectedEmail.is_starred ? "Markierung entfernen" : "Markieren"}
                  >
                    <Star className={cn(
                      "h-4 w-4",
                      selectedEmail.is_starred ? "fill-yellow-400 text-yellow-400" : ""
                    )} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={async () => {
                      if (selectedEmail) {
                        const { error } = await supabase.from('emails').delete().eq('id', selectedEmail.id);
                        if (!error) {
                          setEmails(prev => prev.filter(e => e.id !== selectedEmail.id));
                          setSelectedEmail(null);
                          toast({ title: "Gelöscht", description: "E-Mail wurde gelöscht" });
                        } else {
                          toast({ title: "Fehler", description: "E-Mail konnte nicht gelöscht werden", variant: "destructive" });
                        }
                      }
                    }}
                    title="Löschen"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>

                {/* Attachments Preview - Apple Mail Style */}
                {selectedEmail.attachments && Array.isArray(selectedEmail.attachments) && selectedEmail.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-2">
                      <Paperclip className="h-3 w-3" />
                      {selectedEmail.attachments.length} {selectedEmail.attachments.length === 1 ? 'Anhang' : 'Anhänge'}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {selectedEmail.attachments.map((attachment: any, index: number) => {
                        const isImage = attachment.contentType?.startsWith('image/') || 
                          /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(attachment.filename || '');
                        const isPdf = attachment.contentType?.includes('pdf') || 
                          /\.pdf$/i.test(attachment.filename || '');
                        
                        return (
                          <div
                            key={index}
                            className="group relative bg-muted/50 rounded-xl overflow-hidden border border-border/50 hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer"
                            style={{ width: isImage ? '160px' : '200px' }}
                            onClick={() => openAttachment(attachment, index)}
                          >
                            {/* Preview Area */}
                            <div className={cn(
                              "flex items-center justify-center bg-gradient-to-br",
                              isImage ? "from-blue-500/10 to-purple-500/10 h-24" : 
                              isPdf ? "from-red-500/10 to-orange-500/10 h-20" :
                              "from-gray-500/10 to-slate-500/10 h-20"
                            )}>
                              {isImage ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <FileText className="h-10 w-10 text-blue-500/60" />
                                </div>
                              ) : isPdf ? (
                                <div className="flex flex-col items-center gap-1">
                                  <FileText className="h-10 w-10 text-red-500/70" />
                                  <span className="text-[10px] font-bold text-red-500/70 uppercase">PDF</span>
                                </div>
                              ) : (
                                <FileText className="h-10 w-10 text-muted-foreground/50" />
                              )}
                            </div>
                            
                            {/* Info Area */}
                            <div className="p-3 bg-card/80 backdrop-blur-sm">
                              <p className="text-xs font-medium truncate">
                                {attachment.filename || `Anhang ${index + 1}`}
                              </p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[10px] text-muted-foreground">
                                  {attachment.size ? `${Math.round(attachment.size / 1024)} KB` : 'Größe unbekannt'}
                                </p>
                                <Eye className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>

                              {/* Show if saved to customer - clickable to open quick view */}
                              {attachmentSaves[attachment.filename] && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const saveInfo = attachmentSaves[attachment.filename];
                                    setCustomerQuickViewId(saveInfo.customerId);
                                    setCustomerQuickViewName(saveInfo.customerName);
                                    setCustomerQuickViewOpen(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const saveInfo = attachmentSaves[attachment.filename];
                                      setCustomerQuickViewId(saveInfo.customerId);
                                      setCustomerQuickViewName(saveInfo.customerName);
                                      setCustomerQuickViewOpen(true);
                                    }
                                  }}
                                  className="mt-2 p-1.5 w-full bg-green-500/10 border border-green-500/30 rounded text-[10px] text-green-700 dark:text-green-400 flex items-center gap-1 hover:bg-green-500/20 transition-colors cursor-pointer select-none"
                                >
                                  <FolderUp className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">
                                    Gespeichert bei: {attachmentSaves[attachment.filename].customerName}
                                  </span>
                                </div>
                              )}

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full mt-2 gap-2"
                                onClick={(e) => openSaveAttachmentDialog(attachment, index, e)}
                              >
                                <FolderUp className="h-3 w-3" />
                                {attachmentSaves[attachment.filename] ? 'Erneut speichern' : 'Kundenakte'}
                              </Button>
                            </div>
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Email Body - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 md:p-6">
                  {renderEmailContent()}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full w-full text-center p-8">
              <div>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg font-medium mb-2">Wählen Sie eine E-Mail aus</h3>
                <p className="text-muted-foreground text-sm">
                  Klicken Sie auf eine E-Mail in der Liste, um sie zu lesen.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile FAB */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <Button 
          size="lg" 
          className="h-14 w-14 rounded-full shadow-lg"
          onClick={() => { setReplyData(null); setShowComposeDialog(true); }}
        >
          <Edit className="h-6 w-6" />
        </Button>
      </div>

      {/* Compose Dialog */}
      <EmailComposeDialog
        open={showComposeDialog}
        onOpenChange={(open) => {
          setShowComposeDialog(open);
          if (!open) setReplyData(null);
        }}
        fromAddress={accounts.find(a => a.is_active)?.email_address || accounts[0]?.email_address || ""}
        replyTo={replyData}
        senderName={organization?.name}
        organization={organization || undefined}
        accountId={accounts.find(a => a.is_active)?.id || accounts[0]?.id}
        organizationId={profile?.organization_id}
        onEmailSent={() => loadEmails()}
        userProfile={profile ? {
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone,
          email: profile.email,
          address: profile.address,
          contact_email: profile.contact_email,
          avatar_url: profile.avatar_url
        } : null}
      />

      {/* Attachment Preview Dialog */}
      <Dialog open={attachmentPreview.open} onOpenChange={(open) => setAttachmentPreview(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              {attachmentPreview.filename}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto min-h-0">
            {attachmentPreview.loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-3 text-muted-foreground">Anhang wird geladen...</span>
              </div>
            ) : attachmentPreview.data ? (
              <div className="space-y-4">
                {/* Preview based on content type */}
                {attachmentPreview.contentType.startsWith('image/') ? (
                  <div className="flex justify-center">
                    <img 
                      src={`data:${attachmentPreview.contentType};base64,${attachmentPreview.data}`}
                      alt={attachmentPreview.filename}
                      className="max-w-full max-h-[60vh] object-contain rounded-lg"
                    />
                  </div>
                ) : attachmentPreview.contentType === 'application/pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${attachmentPreview.data}`}
                    className="w-full h-[60vh] rounded-lg border"
                    title={attachmentPreview.filename}
                  />
                ) : attachmentPreview.contentType.startsWith('text/') ? (
                  <pre className="p-4 bg-muted rounded-lg overflow-auto max-h-[60vh] text-sm">
                    {atob(attachmentPreview.data)}
                  </pre>
                ) : (
                  <div className="text-center py-10">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground mb-2">
                      Vorschau für diesen Dateityp nicht verfügbar
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {attachmentPreview.contentType}
                    </p>
                  </div>
                )}
                
                {/* Download Button */}
                <div className="flex justify-center pt-4 border-t">
                  <Button onClick={downloadAttachment} className="gap-2">
                    <Download className="h-4 w-4" />
                    Herunterladen
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-10 text-muted-foreground">
                <X className="h-12 w-12 mx-auto mb-2 opacity-50" />
                Anhang konnte nicht geladen werden
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {selectedEmail && (
        <SaveAttachmentToCustomerDialog
          open={saveAttachmentDialogOpen}
          onOpenChange={(open) => {
            setSaveAttachmentDialogOpen(open);
            if (!open) setAttachmentToSave(null);
          }}
          attachment={attachmentToSave}
          emailId={selectedEmail.id}
          messageId={
            (selectedEmail as any).message_id ||
            (selectedEmail.id.startsWith('imap_search_') ? selectedEmail.id.split('_')[3] : null)
          }
          accountId={
            (selectedEmail as any).account_id ||
            (selectedEmail.id.startsWith('imap_search_') ? selectedEmail.id.split('_')[2] : '') ||
            accounts.find((a) => a.is_active)?.id ||
            accounts[0]?.id ||
            ''
          }
          onSaved={(customerId, customerName, filename) => {
            // Immediately update the attachmentSaves state so the badge appears without re-opening
            console.log('📎 onSaved callback - filename:', filename, 'customer:', customerName);
            setAttachmentSaves(prev => ({
              ...prev,
              [filename]: { customerId, customerName }
            }));
          }}
        />
      )}

      {/* Customer Quick View Dialog */}
      <CustomerQuickViewDialog
        open={customerQuickViewOpen}
        onOpenChange={setCustomerQuickViewOpen}
        customerId={customerQuickViewId}
        customerName={customerQuickViewName}
      />

      {/* Email Re-Auth Dialog */}
      <EmailReAuthDialog
        open={showReAuthDialog}
        onOpenChange={setShowReAuthDialog}
        accountId={reAuthAccountId}
        emailAddress={reAuthEmailAddress}
        onSuccess={() => {
          // Reload emails after successful re-auth
          loadEmails();
          syncEmails();
        }}
      />
    </div>
  );
};

export default ModernEmailClient;
